import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Link as LinkIcon, Users, TrendingUp, Calendar, Globe, Smartphone, Monitor } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { adminAPI } from '../../services/api';
import Card from '../common/Card';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import Button from '../common/Button';
import Table from '../common/Table';
import Badge from '../common/Badge';
import { formatDate } from '../../utils/helpers';

const UserAnalytics = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { executeApiCall } = useApi();

  useEffect(() => {
    if (userId) {
      fetchUserData();
      fetchUserAnalytics();
    }
  }, [userId, selectedPeriod]);

  const fetchUserData = async () => {
    try {
      const result = await executeApiCall(
        () => adminAPI.getUserDetails(userId)
      );
      
      if (result.success) {
        setUserData(result.data);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load user data');
    }
  };

  const fetchUserAnalytics = async () => {
    try {
      const result = await executeApiCall(
        () => adminAPI.getUserUsage(userId, { period: selectedPeriod })
      );
      
      if (result.success) {
        setAnalyticsData(result.data);
      }
    } catch (err) {
      console.error('Error fetching user analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'android':
        return <Smartphone className="h-4 w-4 text-green-600" />;
      case 'ios':
        return <Smartphone className="h-4 w-4 text-blue-600" />;
      case 'desktop':
        return <Monitor className="h-4 w-4 text-purple-600" />;
      default:
        return <Globe className="h-4 w-4 text-ink-600" />;
    }
  };

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'facebook': return 'bg-blue-100 text-blue-800';
      case 'twitter': return 'bg-blue-100 text-blue-800';
      case 'instagram': return 'bg-pink-100 text-pink-800';
      case 'linkedin': return 'bg-blue-100 text-blue-800';
      case 'youtube': return 'bg-red-100 text-red-800';
      case 'tiktok': return 'bg-ink-100 text-ink-800';
      case 'whatsapp': return 'bg-green-100 text-green-800';
      case 'telegram': return 'bg-blue-100 text-blue-800';
      case 'email': return 'bg-purple-100 text-purple-800';
      case 'search': return 'bg-yellow-100 text-yellow-800';
      case 'direct': return 'bg-ink-100 text-ink-800';
      default: return 'bg-ink-100 text-ink-800';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  if (!userData || !analyticsData) {
    return <Alert type="info" message="No data available" />;
  }

  const user = userData.user;
  const summary = analyticsData.summary;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/users')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Users</span>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">User Analytics</h1>
            <p className="text-ink-600 mt-1">
              Analytics for {user?.username} ({user?.email})
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-ink-700">Period:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-1 border border-ink-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* User Info Card */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
              <Users className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">User</p>
              <p className="text-lg font-semibold text-ink-900">{user?.username}</p>
              <p className="text-sm text-ink-500">{user?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100 text-green-600">
              <LinkIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Total Links</p>
              <p className="text-lg font-semibold text-ink-900">{summary?.total_links || 0}</p>
              <p className="text-sm text-ink-500">{summary?.active_links || 0} active</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
              <BarChart3 className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Total Clicks</p>
              <p className="text-lg font-semibold text-ink-900">{summary?.total_clicks || 0}</p>
              <p className="text-sm text-ink-500">All time</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-orange-100 text-orange-600">
              <Calendar className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Member Since</p>
              <p className="text-lg font-semibold text-ink-900">
                {formatDate(user?.created_at).split(' ')[0]}
              </p>
              <p className="text-sm text-ink-500">
                {Math.floor((new Date() - new Date(user?.created_at)) / (1000 * 60 * 60 * 24))} days
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Links Analytics */}
      {analyticsData.links && analyticsData.links.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-ink-900 mb-4">Link Analytics</h3>
          <div className="overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Link</Table.Header>
                  <Table.Header>Short Code</Table.Header>
                  <Table.Header>Clicks</Table.Header>
                  <Table.Header>Created</Table.Header>
                  <Table.Header>Status</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {analyticsData.links.map((link) => (
                  <Table.Row key={link.id}>
                    <Table.Cell>
                      <div className="max-w-xs">
                        <p className="font-medium text-ink-900 truncate">
                          {link.title || 'Untitled'}
                        </p>
                        <p className="text-sm text-ink-500 truncate">
                          {link.original_url}
                        </p>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <code className="text-sm bg-ink-100 px-2 py-1 rounded">
                        {link.short_code}
                      </code>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="font-medium text-ink-900">{link.clicks || 0}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-ink-500">
                        {formatDate(link.created_at)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {link.is_active ? 
                        <Badge color="green" text="Active" /> : 
                        <Badge color="gray" text="Inactive" />
                      }
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Card>
      )}

      {/* Analytics Overview */}
      {analyticsData.analytics && Object.keys(analyticsData.analytics).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold text-ink-900 mb-4">Click Activity (Last {selectedPeriod} days)</h3>
            <div className="space-y-3">
              {Object.entries(analyticsData.analytics)
                .sort(([a], [b]) => new Date(b) - new Date(a))
                .slice(0, 10)
                .map(([date, data]) => (
                  <div key={date} className="flex justify-between items-center p-2 bg-ink-50 rounded">
                    <span className="text-sm font-medium text-ink-900">{date}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-ink-600">{data.clicks} clicks</span>
                      <span className="text-sm text-ink-500">{data.unique_visitors} unique</span>
                    </div>
                  </div>
                ))}
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-ink-900 mb-4">Analytics Summary</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-ink-600">Total Clicks (Period)</span>
                <span className="text-sm font-medium text-ink-900">
                  {Object.values(analyticsData.analytics).reduce((sum, data) => sum + data.clicks, 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-ink-600">Unique Visitors (Period)</span>
                <span className="text-sm font-medium text-ink-900">
                  {Object.values(analyticsData.analytics).reduce((sum, data) => sum + data.unique_visitors, 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-ink-600">Active Days</span>
                <span className="text-sm font-medium text-ink-900">
                  {Object.keys(analyticsData.analytics).length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-ink-600">Average Clicks/Day</span>
                <span className="text-sm font-medium text-ink-900">
                  {Object.keys(analyticsData.analytics).length > 0 
                    ? Math.round(Object.values(analyticsData.analytics).reduce((sum, data) => sum + data.clicks, 0) / Object.keys(analyticsData.analytics).length)
                    : 0
                  }
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* No Data State */}
      {(!analyticsData.links || analyticsData.links.length === 0) && (
        <Card>
          <div className="text-center py-8">
            <BarChart3 className="h-12 w-12 text-ink-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-ink-900 mb-2">No Analytics Data</h3>
            <p className="text-ink-500">
              This user hasn't created any links yet or there's no analytics data available.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UserAnalytics;
